/**
 * IntroVideoPage — trang nộp + quản lý Video giới thiệu đội ở Vòng Sơ loại (R1).
 *
 * - Member đội: thấy upload form cho đội mình (Điều 9.3/9.4).
 * - BTC/Admin/Judge: thấy bảng tất cả đội + trạng thái nộp video + nút phát/tải để
 *   review trước khi chấm tie-break Điều 10.3.
 */
import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { Movie, PlayArrow, Download, Cancel, Star } from '@mui/icons-material'

import { useAppSelector } from '../hooks/redux'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { hasPermission } from '../utils/permissions'
import { PageHeader } from '../components/PageHeader'
import { R1IntroVideoUpload } from '../components/R1IntroVideoUpload'
import VideoTiebreakDialog, { type VideoTiebreakDialogTeam } from '../components/VideoTiebreakDialog'
import { API_BASE_URL, buildApiUrl } from '../config/api'
import { getFilterPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'

export default function IntroVideoPage() {
  const { user: currentUser, token } = useAppSelector((state) => state.auth)
  const { data: teams = [], isLoading } = useGetTeamsQuery()

  const canSubmit = currentUser && hasPermission(currentUser.role, 'SUBMIT_MEMORIAL')
  const canReview =
    currentUser &&
    (hasPermission(currentUser.role, 'MANAGE_USERS') ||
      hasPermission(currentUser.role, 'ASSIGN_CASES') ||
      hasPermission(currentUser.role, 'SCORE_CASES'))
  const userTeam = teams.find((t) => t.members.includes(currentUser?.id || ''))

  const [filter, setFilter] = useState('')
  const [playTeamId, setPlayTeamId] = useState<string | null>(null)
  // F.1 — chấm điểm video tie-break (chỉ ASSIGN_CASES — secretary admin).
  const canScoreVideoTiebreak =
    currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const [tiebreakTeam, setTiebreakTeam] = useState<VideoTiebreakDialogTeam | null>(null)

  const filteredTeams = useMemo(() => {
    if (!filter.trim()) return teams
    const q = filter.toLowerCase()
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.university || '').toLowerCase().includes(q),
    )
  }, [teams, filter])

  const streamUrl = (teamId: string) =>
    buildApiUrl(API_BASE_URL, `/teams/${teamId}/intro-video/stream`)

  // FileResponse cần Authorization header — fetch blob rồi mở.
  const handleOpenVideo = async (teamId: string, mode: 'play' | 'download') => {
    try {
      const res = await fetch(streamUrl(teamId), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      if (mode === 'play') {
        setPlayTeamId(teamId)
        ;(window as any).__playUrl = url
        setTimeout(() => {
          const el = document.getElementById(`intro-video-player-${teamId}`) as HTMLVideoElement | null
          if (el) {
            el.src = url
            el.play().catch(() => {})
          }
        }, 100)
      } else {
        const a = document.createElement('a')
        a.href = url
        const team = teams.find((t) => t.id === teamId)
        a.download = (team?.intro_video as any)?.file_name || `${team?.name || teamId}-intro.mp4`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      alert(e?.message || 'Không tải được video')
    }
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Video giới thiệu đội"
        subtitle="Vòng Sơ loại — nộp video clip 10 phút giới thiệu đội thi (Điều 9.3, dùng cho tie-break Điều 10.3)."
        icon={<Movie />}
      />

      {canSubmit && !isLoading && !userTeam && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bạn chưa thuộc đội thi nào. Hãy hoàn tất đăng ký đội trước.
        </Alert>
      )}

      {canSubmit && userTeam && (
        <Box sx={{ mb: 4 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Yêu cầu:</strong> 1 video clip giới thiệu đội (≤ 10 phút, MP4/WebM/MOV, ≤ 200MB).
              Nội dung gồm: giới thiệu tên đội, thành viên, định hướng pháp lý chuyên môn,
              điểm mạnh đội thi.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Áp dụng:</strong> Theo Điều 10.3 thể lệ — nếu Vòng Sơ loại có hai đội
              cùng tổng điểm, BTC sẽ chấm phụ video clip này (3 tiêu chí: sáng tạo,
              trình bày, chuyên nghiệp — mỗi tiêu chí 0–10đ) để phân hạng.
            </Typography>
          </Alert>
          <R1IntroVideoUpload team={userTeam} isOwnTeam />
        </Box>
      )}

      {canReview && (
        <>
          <Paper elevation={0} sx={(t) => ({ ...getFilterPaperSx(t), mb: 2 })}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                Video các đội thi ({filteredTeams.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Tìm theo tên đội / trường..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            </Stack>
          </Paper>

          <TableContainer component={Paper} elevation={0} sx={(t) => getTablePaperSx(t)}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Đội</strong></TableCell>
                  <TableCell><strong>Trường</strong></TableCell>
                  <TableCell><strong>Trạng thái</strong></TableCell>
                  <TableCell><strong>File</strong></TableCell>
                  <TableCell><strong>Ngày nộp</strong></TableCell>
                  <TableCell><strong>Điểm tie-break</strong></TableCell>
                  <TableCell align="right"><strong>Thao tác</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTeams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        Không có đội nào.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTeams.map((team) => {
                    const v = (team as any).intro_video as
                      | { file_name?: string; file_size?: number | string; uploaded_at?: string }
                      | null
                      | undefined
                    const hasVideo = !!v?.file_name
                    return (
                      <TableRow key={team.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {team.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {team.university || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {hasVideo ? (
                            <Chip size="small" label="Đã nộp" color="success" />
                          ) : (
                            <Chip size="small" label="Chưa nộp" color="warning" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {hasVideo ? (
                            <Typography variant="caption">
                              {v?.file_name}
                              {v?.file_size
                                ? ` · ${(Number(v.file_size) / 1024 / 1024).toFixed(1)}MB`
                                : ''}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {v?.uploaded_at ? (
                            <Typography variant="caption">
                              {new Date(v.uploaded_at).toLocaleString('vi-VN')}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {/* F.1 — Điểm tie-break (tổng /30, chỉ tie-break phụ) */}
                          {(() => {
                            const tb = (team as any).video_tiebreak
                            const totalTb = (team as any).video_tiebreak_total
                            if (totalTb != null) {
                              return (
                                <Chip
                                  size="small"
                                  color="warning"
                                  label={`${Number(totalTb).toFixed(1)} / 30`}
                                />
                              )
                            }
                            if (tb && (tb.creativity || tb.presentation || tb.professionalism)) {
                              const sum =
                                Number(tb.creativity || 0) +
                                Number(tb.presentation || 0) +
                                Number(tb.professionalism || 0)
                              return (
                                <Chip size="small" color="warning" label={`${sum.toFixed(1)} / 30`} />
                              )
                            }
                            return (
                              <Typography variant="caption" color="text.secondary">
                                Chưa chấm
                              </Typography>
                            )
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          {hasVideo ? (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton
                                size="small"
                                color="primary"
                                title="Phát video"
                                onClick={() => handleOpenVideo(team.id, 'play')}
                              >
                                <PlayArrow fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                title="Tải về"
                                onClick={() => handleOpenVideo(team.id, 'download')}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                              {/* F.1 — chấm điểm tie-break (admin only) */}
                              {canScoreVideoTiebreak && (
                                <IconButton
                                  size="small"
                                  color="warning"
                                  title="Chấm điểm tie-break (Điều 10.3)"
                                  onClick={() =>
                                    setTiebreakTeam({
                                      id: team.id,
                                      name: team.name,
                                      intro_video: (team as any).intro_video,
                                      video_tiebreak: (team as any).video_tiebreak,
                                    })
                                  }
                                >
                                  <Star fontSize="small" />
                                </IconButton>
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!canSubmit && !canReview && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Bạn không có quyền xem/nộp video giới thiệu ở trang này.
        </Alert>
      )}

      {/* Dialog phát video inline */}
      <Dialog
        open={!!playTeamId}
        onClose={() => {
          setPlayTeamId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Movie color="primary" />
            <span>
              {teams.find((t) => t.id === playTeamId)?.name || 'Video giới thiệu'}
            </span>
          </Box>
        </DialogTitle>
        <DialogContent>
          {playTeamId && (
            <Box sx={{ width: '100%' }}>
              <video
                id={`intro-video-player-${playTeamId}`}
                controls
                style={{ width: '100%', maxHeight: 480, background: '#000' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayTeamId(null)} startIcon={<Cancel />}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* F.1 — Tie-break scoring dialog (admin only) */}
      <VideoTiebreakDialog
        team={tiebreakTeam}
        onClose={() => setTiebreakTeam(null)}
        onSaved={() => {
          // teams query sẽ revalidate sau invalidatesTags ['Team']
        }}
      />
    </Container>
  )
}
